import React, { FC, useContext, useEffect, useState } from "react";
import Select from "../../ui/Select";
import { Editor, OnValidate } from "@monaco-editor/react";
import Button from "../../ui/Button";
import { FaPlusCircle } from "react-icons/fa";
import Modal from "../../ui/Modal";
import { ActionType, IAction, IFlow, IFlowExecution } from "../../../types/ApiTypes";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import axios from "axios";
import { AdminContext } from "../../../context/AdminContext";
import useApi from "../../../hooks/useApi";
import handleToastError from "../../../helpers/handleToastError";

interface Props {
  isOpen?: boolean;
  spaceTypes: { label: string, value: number }[],
  actions: { label: string, value: ActionType }[]
  flowPolicy?: IFlowExecution
  flowsList: { label: string, value: IFlow }[];
  onClose?: () => void;
}

interface InputValues {
  action: string;
  spaceType: number;
  flow: number;
  extraPayload: string;
}


const FlowPolicyModal: FC<Props> = (props) => {
  const admCtx = useContext(AdminContext);
  const { handleSubmit, control, setValue, reset } = useForm<InputValues>();
  const { createFlowPolicy, editFlowPolicy } = useApi()

  const [isOpen, setIsOpen] = useState(false);
  const [flowPayloadErrors, setFlowPayloadErrors] = useState<string[]>([]);

  useEffect(() => {
    setIsOpen(props.isOpen || false);
    if (props.flowPolicy) {
      setValue("action", props.flowPolicy.action);
      setValue("flow", props.flowPolicy.flow.id);
      setValue("spaceType", props.flowPolicy.group_type);
      setValue("extraPayload", props.flowPolicy.extraPayload || "");
    } else {
      reset();
    }
  }, [props.isOpen, props.flowPolicy]);

  const onValidate: OnValidate = (markers) => {
    const messages: string[] = markers.map(el => el.message);
    setFlowPayloadErrors(messages);
  };

  const onSubmit: SubmitHandler<InputValues> = async (e) => {
    if (flowPayloadErrors.length) return;
    let biggestOrder = 0;
    if (admCtx.state.policies.length > 0) {
      biggestOrder = admCtx.state.policies.reduce((value, current) => {
        if (current.action !== e.action || current.group_type !== e.spaceType) return value;
        return Math.max(value, current.order);
      }, 0);
    }
    biggestOrder++;
    const payload = {
      flowId: e.flow,
      order: props.flowPolicy ? props.flowPolicy.order : biggestOrder,
      actionType: e.action,
      groupType: e.spaceType,
      extraPayload: e.extraPayload || undefined
    };
    const flowPolicy = await toast.promise(props.flowPolicy ? editFlowPolicy({ id: props.flowPolicy.id, ...payload }) : createFlowPolicy(payload), {
      loading: "Der Formular wird abgeschickt",
      success: <span className="text-sm">Die Policy wurde {props.flowPolicy ? 'bearbeitet' : 'erstellt'}</span>,
      error: (err) => handleToastError(err, `Die Policy konnte nicht ${props.flowPolicy ? 'bearbeitet' : 'erstellt'} werden.`)
    });
    props.flowPolicy ? admCtx.dispatch({ type: "UPDATE_POLICY", payload: { updatedPolicy: flowPolicy, policyId: flowPolicy.id } }) : admCtx.dispatch({ type: "APPEND_POLICY", payload: flowPolicy })
    setIsOpen(false);
    props.onClose && props.onClose();
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}><FaPlusCircle className="mr-2" /> Neue Policy
        erstellen</Button>
      <Modal isOpen={isOpen} onClose={() => {
        setIsOpen(false);
        if(props.onClose) props.onClose();
      }} title="Neues Flow erstellen">
        <form className="space-y-2" onSubmit={handleSubmit(onSubmit)}>
          <small>Die erstellten Policies werden immer am Ende ausgeführt</small>
          <div>
            <label htmlFor="flow" className="font-semibold">Flow</label>
            <Controller name="flow" rules={{required: true}} control={control} render={({ field, fieldState }) => (
              <Select options={props.flowsList} onChange={(e) => field.onChange(e.value.id)}
                      placeholder="Wähle das Flow" defaultValue={props.flowsList.find(x => x.value.id === props.flowPolicy?.flow.id)}/>
            )} />
          </div>
          <div>
            <label htmlFor="action" className="font-semibold">Aktion</label>
            <Controller name="action" rules={{required: true}} control={control} render={({ field, fieldState }) => (
              <Select options={props.actions} onChange={(e) => field.onChange(e.value)}
                      placeholder="Wähle den Trigger" defaultValue={props.actions.find(x => x.value === props.flowPolicy?.action)}/>
            )} />
          </div>
          <div>
            <label htmlFor="spaceType" className="font-semibold">Spacetyp</label>
            <Controller name="spaceType" rules={{required: true}} control={control} render={({ field, fieldState }) => (
              <Select<number> options={props.spaceTypes} onChange={(e) => field.onChange(e.value)}
                              placeholder="Wähle den Spacetyp" defaultValue={props.spaceTypes.find(x => x.value === props.flowPolicy?.group_type)}/>
            )} />
          </div>
          <div>
            <label htmlFor="extraPayload" className="font-semibold">Payload</label>
            <div className="overflow-hidden rounded-md">
              <Controller name="extraPayload" control={control} render={({ field, fieldState }) => (
                <Editor height="14rem" defaultLanguage="json" defaultValue="" theme="vs-dark"
                        onChange={(e) => field.onChange(e)} value={field.value} onValidate={onValidate} />
              )}/>
            </div>
            {!flowPayloadErrors.length &&
              <small>Extra payload in JSON-Format welches an das Flow weitergegeben wird</small>}
            {flowPayloadErrors.length > 0 && (
              <div className="flex flex-col gap-1">
                {flowPayloadErrors.map(el => (
                  <small key={el} className="text-red-500">{el}</small>
                ))}
              </div>
            )}
          </div>
          <Button type="submit"><FaPlusCircle className="mr-2" />{props.flowPolicy ? 'Speichern' : 'Erstellen'}</Button>
        </form>
      </Modal>
    </>
  );
};

export default FlowPolicyModal;